import { jsPDF } from 'jspdf';

const DOC_TITLES = {
  sick_leave: 'Sick Leave Certificate',
  referral: 'Referral Letter',
  lab_order: 'Lab Request',
  radiology_request: 'Radiology Request',
};

function addWrapped(doc, text, x, y, maxWidth, lineHeight = 6) {
  const lines = doc.splitTextToSize(String(text || '—'), maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function ensureSpace(doc, y, needed = 28) {
  if (y <= 280 - needed) return y;
  doc.addPage();
  return 20;
}

function rowsForDocument(type, data = {}) {
  switch (type) {
    case 'sick_leave':
      return [
        ['Diagnosis', data.diagnosis],
        ['Days', data.days != null ? String(data.days) : ''],
      ];
    case 'referral':
      return [
        ['Specialty', data.specialty],
        ['Reason', data.reason],
        ['Urgency', data.urgency || 'routine'],
      ];
    case 'lab_order':
      return [
        ['Tests', Array.isArray(data.tests) ? data.tests.join(', ') : data.tests],
        ['Indication', data.indication],
        ['Urgency', data.urgency || 'routine'],
      ];
    case 'radiology_request':
      return [
        ['Investigation', data.investigation],
        ['Body part', data.body_part],
        ['Indication', data.indication],
        ['Urgency', data.urgency || 'routine'],
      ];
    default:
      return Object.entries(data)
        .filter(([key]) => key !== 'needed')
        .map(([key, value]) => [
          key.replaceAll('_', ' '),
          Array.isArray(value) ? value.join(', ') : value,
        ]);
  }
}

function logoFormat(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'JPEG';
  if (dataUrl.includes('image/png')) return 'PNG';
  if (dataUrl.includes('image/webp')) return 'WEBP';
  return 'JPEG';
}

function drawClinicHeader(doc, {
  margin,
  pageWidth,
  maxWidth,
  clinicName = '',
  clinicLogoUrl = '',
  y = 16,
}) {
  let nextY = y;
  const hasLogo = Boolean(clinicLogoUrl && String(clinicLogoUrl).startsWith('data:image/'));
  const brand = clinicName || 'Sawtna';

  if (hasLogo) {
    try {
      const logoW = 18;
      const logoH = 18;
      doc.addImage(clinicLogoUrl, logoFormat(clinicLogoUrl), margin, nextY - 4, logoW, logoH);
      doc.setFontSize(16);
      doc.setTextColor(17, 24, 39);
      doc.setFont(undefined, 'bold');
      doc.text(brand, margin + logoW + 4, nextY + 8);
      nextY += logoH + 4;
    } catch {
      doc.setFontSize(16);
      doc.setTextColor(17, 24, 39);
      doc.setFont(undefined, 'bold');
      nextY = addWrapped(doc, brand, margin, nextY + 4, maxWidth, 7);
    }
  } else {
    doc.setFontSize(16);
    doc.setTextColor(17, 24, 39);
    doc.setFont(undefined, 'bold');
    nextY = addWrapped(doc, brand, margin, nextY + 4, maxWidth, 7);
  }

  doc.setFont(undefined, 'normal');
  doc.setDrawColor(30, 64, 175);
  doc.line(margin, nextY + 4, pageWidth - margin, nextY + 4);
  return nextY + 12;
}

export function getDoctorDocumentTitle(type) {
  return DOC_TITLES[type] || String(type || 'Document').replaceAll('_', ' ');
}

/** Build and download a PDF for a generated doctor document. */
export function generateDoctorDocumentPDF({
  type,
  data,
  patientName = '',
  doctorName = '',
  clinicName = '',
  clinicLogoUrl = '',
}) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - margin * 2;
  let y = drawClinicHeader(doc, {
    margin,
    pageWidth,
    maxWidth,
    clinicName,
    clinicLogoUrl,
  });
  const title = getDoctorDocumentTitle(type);

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont(undefined, 'bold');
  y = addWrapped(doc, title, margin, y, maxWidth, 7) + 4;
  doc.setFont(undefined, 'normal');

  doc.setFontSize(11);
  doc.setTextColor(60, 60, 60);
  if (patientName) {
    y = addWrapped(doc, `Patient: ${patientName}`, margin, y, maxWidth);
  }
  if (doctorName) {
    y = addWrapped(doc, `Doctor: ${doctorName}`, margin, y, maxWidth);
  }
  y = addWrapped(doc, `Date: ${new Date().toLocaleDateString()}`, margin, y, maxWidth) + 4;

  doc.setDrawColor(229, 231, 235);
  doc.line(margin, y, pageWidth - margin, y);
  y += 10;

  rowsForDocument(type, data).forEach(([label, value]) => {
    y = ensureSpace(doc, y);
    doc.setFont(undefined, 'bold');
    y = addWrapped(doc, label, margin, y, maxWidth);
    doc.setFont(undefined, 'normal');
    y = addWrapped(doc, value == null || value === '' ? '—' : value, margin, y, maxWidth) + 4;
  });

  const safeType = String(type || 'document').replace(/[^\w-]+/g, '_');
  const safePatient = String(patientName || 'patient').replace(/[^\w-]+/g, '_');
  doc.save(`Sawtna_${safeType}_${safePatient}.pdf`);
}
